import {useEffect, useState} from "react";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {MapSectorInfo, MapSectorInfoValue} from "../../../services/models/map_sector";
import {MapSettingsColor} from "../../../services/models/map_settings";
import {CELL_SIZE, GAP, getElementAt, getSectorColor, GRID_COLUMNS, isDarkColor, mapGrid} from "../../AdminComponents/map-of-subjects/utils";
import styles from "../../AdminComponents/map-of-subjects/MapOfSubjectsView.module.css";

interface Props {
    sectors: MapSectorInfo[];
    dashboardData: Array<{ _id: string; value: number | null }>;
    onSectorClick?: (sectorId: string, value: number | null) => void;
    title: string;
    colorSettings?: MapSettingsColor[];
}

export const Form30DashboardMap = ({sectors, dashboardData, onSectorClick, title, colorSettings}: Props) => {
    const [sectorsValues, setSectorsValues] = useState<MapSectorInfoValue[]>([]);
    const [avgValue, setAvgValue] = useState(0);

    useEffect(() => {
        if (dashboardData && sectors?.length) {
            const result: MapSectorInfoValue[] = [];
            
            // Фильтруем данные для расчета среднего (только положительные значения)
            const filteredData = dashboardData
                .map(item => item.value)
                .filter((val): val is number => val !== null && val > 0);
            
            const avg = filteredData.length > 0 
                ? filteredData.reduce((a, b) => a + b, 0) / filteredData.length 
                : 0;

            for (const element of sectors) {
                const findedData = dashboardData.find(item => item._id === element.sectorId);

                if (findedData && findedData.value !== null) {
                    result.push({
                        sectorId: element.sectorId,
                        value: findedData.value.toString(),
                        title: title,
                    });
                }
            }

            setAvgValue(avg);
            setSectorsValues(result);
        }
    }, [sectors, dashboardData, title]);

    const renderTooltip = (tooltip: string) => (props: React.ComponentPropsWithoutRef<typeof Tooltip>) =>
        (
            <Tooltip id="button-tooltip" {...props}>
                {tooltip}
            </Tooltip>
        );

    const handleClick = (sectorId: string, value: string | undefined) => {
        if (onSectorClick) {
            const numValue = value ? Number(value) : null;
            onSectorClick(sectorId, numValue);
        }
    };

    return (
        <div
            className={styles.wrapper}
            style={{
                width: `${GRID_COLUMNS * (CELL_SIZE + GAP)}px`,
                gridTemplateColumns: `repeat(${GRID_COLUMNS}, ${CELL_SIZE}px)`,
                gap: GAP,
            }}
        >
            {mapGrid.map(({x, y}, index) => {
                const element = getElementAt(sectors, x, y);
                const valueInfo = sectorsValues.find(({sectorId}) => sectorId === element?.sectorId);
                const isValueExists = valueInfo?.value !== "-" && valueInfo?.value !== "";
                
                // Определяем цвет: используем настройки из БД если есть, иначе белый
                let color = "#fff";
                if (valueInfo?.value === undefined) {
                    color = "#ededed";
                } else if (isValueExists && colorSettings && colorSettings.length > 0) {
                    color = getSectorColor(colorSettings, Number(valueInfo.value));
                } else if (!isValueExists) {
                    color = "#fff";
                }
                
                const tooltip = valueInfo && isValueExists ? `${valueInfo.title}: ${valueInfo.value}` : "Нет данных";

                return (
                    <div
                        key={index}
                        className={styles.mapItem}
                        style={{
                            width: CELL_SIZE,
                            height: CELL_SIZE,
                            border: element ? "1px solid #aaa" : "none",
                        }}
                    >
                        {element && (
                            <OverlayTrigger placement="top" delay={{show: 250, hide: 250}}
                                            overlay={renderTooltip(tooltip)}>
                                <div
                                    className={styles.mapItemLink}
                                    style={{
                                        backgroundColor: element ? color : "transparent",
                                        color: isDarkColor(color) ? "#ddd" : "#222",
                                        cursor: onSectorClick ? 'pointer' : 'default'
                                    }}
                                    onClick={() => handleClick(element.sectorId, valueInfo?.value)}
                                >
                                    <strong>{element.shortName}</strong>
                                    {valueInfo?.value === undefined ? (
                                        <span style={{visibility: "hidden"}}>-</span>
                                    ) : (
                                        <span>{valueInfo.value || "-"}</span>
                                    )}
                                </div>
                            </OverlayTrigger>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
